import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, HStack, IconButton, Spinner } from '@chakra-ui/react';
import { getTrackPathPrefix } from '@src/components/sco/Education/ContentTrackContext';
import { QueryContainer } from '@src/components/sco/QueryContainer';
import { ChevronLeftIcon, ChevronRightIcon } from '@src/components/sco/icons';
import { ContentTrack, ContentTrackSection, ContentTrackStep } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
type StepNavigatorProps = {
  beforeNextClick?: () => Promise<boolean>;
  beforePreviousClick?: () => Promise<boolean>;
  currentContentTrackStep: ContentTrackStep;
  miniNav?: boolean;
};
export const StepNavigator = props => {
  const $ = _c(37);
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = StepNavigator.queries.contentTrack();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.currentContentTrackStep.parentContentTrackSection.parentContentTrack.slug) {
    t1 = {
      variables: {
        contentTrackSlug: props.currentContentTrackStep.parentContentTrackSection.parentContentTrack.slug
      }
    };
    $[1] = props.currentContentTrackStep.parentContentTrackSection.parentContentTrack.slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const [nextStep, setNextStep] = useState();
  const [nextSection, setNextSection] = useState();
  const [previousStep, setPreviousStep] = useState();
  const [previousSection, setPreviousSection] = useState();
  let t2;
  if ($[3] !== props.currentContentTrackStep || $[4] !== query.data?.contentTrack) {
    t2 = () => {
      if (!props.currentContentTrackStep || !query.data?.contentTrack) {
        return;
      }
      const next = determineNext({
        track: query.data?.contentTrack,
        currentStep: props.currentContentTrackStep
      });
      setNextSection(next.section);
      setNextStep(next.step);
      const previous = determinePrevious({
        track: query.data?.contentTrack,
        currentStep: props.currentContentTrackStep
      });
      setPreviousSection(previous.section);
      setPreviousStep(previous.step);
    };
    $[3] = props.currentContentTrackStep;
    $[4] = query.data?.contentTrack;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = query.data?.contentTrack?.id;
  let t4;
  if ($[6] !== props.currentContentTrackStep.id || $[7] !== t3) {
    t4 = [props.currentContentTrackStep.id, t3];
    $[6] = props.currentContentTrackStep.id;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t2, t4);
  let t5;
  if ($[9] !== previousSection?.slug || $[10] !== previousStep?.slug || $[11] !== query.data?.contentTrack) {
    t5 = () => `/${getTrackPathPrefix(query.data?.contentTrack)}/${[query.data?.contentTrack?.slug, previousSection?.slug, previousStep?.slug].filter(Boolean).join("/")}`;
    $[9] = previousSection?.slug;
    $[10] = previousStep?.slug;
    $[11] = query.data?.contentTrack;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const getBackHref = t5;
  let t6;
  if ($[13] !== nextSection?.slug || $[14] !== nextStep?.slug || $[15] !== query.data?.contentTrack) {
    t6 = () => `/${getTrackPathPrefix(query.data?.contentTrack)}/${[query.data?.contentTrack?.slug, nextSection?.slug, nextStep?.slug].filter(Boolean).join("/")}`;
    $[13] = nextSection?.slug;
    $[14] = nextStep?.slug;
    $[15] = query.data?.contentTrack;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const getNextHref = t6;
  let t7;
  if ($[17] !== getBackHref || $[18] !== props || $[19] !== router) {
    t7 = async () => {
      if (props.beforePreviousClick) {
        await props.beforePreviousClick();
      }
      router.push(getBackHref());
    };
    $[17] = getBackHref;
    $[18] = props;
    $[19] = router;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const goBack = t7;
  let t8;
  if ($[21] !== getNextHref || $[22] !== props || $[23] !== router) {
    t8 = async () => {
      if (props.beforeNextClick) {
        await props.beforeNextClick();
      }
      router.push(getNextHref());
    };
    $[21] = getNextHref;
    $[22] = props;
    $[23] = router;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const goNext = t8;
  const t9 = props.miniNav ? 0 : 0;
  let t10;
  if ($[25] !== getBackHref || $[26] !== getNextHref || $[27] !== goBack || $[28] !== goNext || $[29] !== props.miniNav) {
    t10 = data => <>{props.miniNav ? <HStack justify="flex-end"><IconButton asChild={true} aria-label="Previous" size="sm" variant="outline"><a href={getBackHref()} onClick={e => {
            e.preventDefault();
            goBack();
          }}><ChevronLeftIcon /></a></IconButton><Button asChild={true} size={props.miniNav ? "sm" : "lg"} variant="cta"><a href={getNextHref()} onClick={e_0 => {
            e_0.preventDefault();
            goNext();
          }}>Next<ChevronRightIcon /></a></Button></HStack> : <HStack justify="space-between"><Button asChild={true} size={props.miniNav ? "sm" : "lg"} variant="outline"><a href={getBackHref()} onClick={e_1 => {
            e_1.preventDefault();
            goBack();
          }}>Back</a></Button><Button asChild={true} bgColor="actionActive" color="black" size={props.miniNav ? "sm" : "lg"}><a href={getNextHref()} onClick={e_2 => {
            e_2.preventDefault();
            goNext();
          }}>Next<ChevronRightIcon /></a></Button></HStack>}</>;
    $[25] = getBackHref;
    $[26] = getNextHref;
    $[27] = goBack;
    $[28] = goNext;
    $[29] = props.miniNav;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== query || $[32] !== t10) {
    t11 = <QueryContainer query={query} renderError={_temp} renderData={t10} renderLoading={_temp2} />;
    $[31] = query;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t11 || $[35] !== t9) {
    t12 = <Box paddingY={t9}>{t11}</Box>;
    $[34] = t11;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};
StepNavigator.queries = {
  contentTrack: () => gql`
    query ($contentTrackSlug: String!) {
      contentTrack(where: { slug: $contentTrackSlug }) {
        id
        slug
        type
        color {
          hex
        }
        sections {
          id
          slug
          steps {
            id
            slug
          }
        }
      }
    }
  `
};
function determineNext({
  track,
  currentStep
}: {
  track: ContentTrack;
  currentStep: ContentTrackStep;
}): {
  section: ContentTrackSection;
  step: ContentTrackStep;
} {
  let section,
    step = null;
  const currentSectionIndex = track.sections.findIndex(s => s.id === currentStep.parentContentTrackSection.id);
  const currentStepIndex = currentSectionIndex !== null ? track.sections[currentSectionIndex].steps.findIndex(s => s.id === currentStep.id) : null;

  // Current step has next step in its section
  if (track.sections[currentSectionIndex].steps[currentStepIndex + 1]) {
    section = track.sections[currentSectionIndex];
    step = section.steps[currentStepIndex + 1];
  }

  // Current step is last step in its section
  if (track.sections[currentSectionIndex].steps.length - 1 === currentStepIndex) {
    section = track.sections[currentSectionIndex + 1];
    step = section?.steps[0];
  }
  return {
    section,
    step
  };
}
function determinePrevious({
  track,
  currentStep
}: {
  track: ContentTrack;
  currentStep: ContentTrackStep;
}): {
  section: ContentTrackSection;
  step: ContentTrackStep;
} {
  let section,
    step = null;
  const currentSectionIndex = track.sections.findIndex(s => s.id === currentStep.parentContentTrackSection.id);
  const currentStepIndex = currentSectionIndex !== null ? track.sections[currentSectionIndex].steps.findIndex(s => s.id === currentStep.id) : null;

  // Current step has previous step in its section
  if (track.sections[currentSectionIndex].steps[currentStepIndex - 1]) {
    section = track.sections[currentSectionIndex];
    step = section.steps[currentStepIndex - 1];
  }

  // Current step is first step in its section
  if (currentStepIndex === 0) {
    section = track.sections[currentSectionIndex - 1];
    step = section?.steps[0];
  }
  return {
    section,
    step
  };
}
function _temp() {
  return <Center>Error loading StepNavigator data.</Center>;
}
function _temp2() {
  return <Center><Spinner /></Center>;
}